import { useEffect, useRef } from 'react'
import { io, type Socket } from 'socket.io-client'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { env } from '@/config/env'
import { queryKeys } from '@/api/query-keys'
import { useSessionStore } from '@/stores/useSessionStore'
import {
  isDevNotificationBackendDisabled,
  markDevNotificationBackendUnavailable,
  resetNotificationDevBackendForTests,
} from '@/features/notifications/lib/notificationDevBackend'
import { logger } from '@/lib/logger'

/**
 * One browser session shares one Socket.io connection per (origin, subdomain).
 * Avoids Vite WS proxy `write ECONNABORTED` when React Strict Mode runs cleanup→remount in the
 * same task: ref-count + `queueMicrotask` deferral keeps the socket alive until no subscribers.
 */
type SharedBellSocket = { socket: Socket; key: string }

let sharedBellSocket: SharedBellSocket | null = null
let sharedBellSocketRefCount = 0

/** Clears module-level socket state. Vitest only — keeps test cases isolated. */
export function resetNotificationSocketSingletonForTests(): void {
  if (sharedBellSocket) {
    try {
      sharedBellSocket.socket.disconnect()
    } catch {
      /* noop */
    }
  }
  sharedBellSocket = null
  sharedBellSocketRefCount = 0
  resetNotificationDevBackendForTests()
}

function socketKey(wsUrl: string, subdomain: string): string {
  return `${wsUrl}\0${subdomain}`
}

function retainBellSocket(wsUrl: string, subdomain: string): Socket {
  const key = socketKey(wsUrl, subdomain)
  sharedBellSocketRefCount++
  if (sharedBellSocket?.key === key) {
    return sharedBellSocket.socket
  }
  if (sharedBellSocket) {
    sharedBellSocket.socket.disconnect()
    sharedBellSocket = null
  }
  const socket = io(wsUrl, {
    withCredentials: true,
    auth: { subdomain },
    transports: ['websocket', 'polling'],
    reconnection: import.meta.env.DEV ? !isDevNotificationBackendDisabled() : true,
    reconnectionAttempts: import.meta.env.DEV ? 2 : 5,
    reconnectionDelay: 2000,
  })
  if (import.meta.env.DEV && !isDevNotificationBackendDisabled()) {
    let devConnectErrorHandled = false
    socket.on('connect_error', (err: Error) => {
      if (devConnectErrorHandled) return
      devConnectErrorHandled = true
      markDevNotificationBackendUnavailable(err)
      socket.io.opts.reconnection = false
      socket.disconnect()
    })
  }
  sharedBellSocket = { socket, key }
  return socket
}

function releaseBellSocket(): void {
  sharedBellSocketRefCount--
  queueMicrotask(() => {
    if (sharedBellSocketRefCount <= 0 && sharedBellSocket) {
      sharedBellSocket.socket.disconnect()
      sharedBellSocket = null
    }
  })
}

/**
 * Base URL for Socket.io (notification service).
 *
 * - **Empty env:** use the SPA origin so the browser sends the same HttpOnly cookies as
 *   `/api/v1/*` (Vite proxies `/socket.io` → notification :4005 in dev).
 * - **Explicit env:** used as-is in production (put the socket behind the same gateway host
 *   as the SPA, or ensure cookie `Domain` covers both origins).
 * - **Dev + cross-origin explicit URL** (e.g. app on `acme.localhost:3000`, env `http://localhost:4005`):
 *   cookies are not sent → fall back to SPA origin so the Vite proxy can attach them.
 */
function getNotificationSocketBaseUrl(): string {
  const configured = env.VITE_NOTIFICATION_SERVICE_WS_URL.trim()
  if (!globalThis.window?.location) {
    return configured
  }
  const origin = globalThis.window.location.origin
  if (!configured) {
    return origin
  }
  try {
    const cfgOrigin = new URL(configured).origin
    if (cfgOrigin !== origin && import.meta.env.DEV) {
      logger.warn('notification_socket.cross_origin_dev', {
        message:
          'Using SPA origin for Socket.io so auth cookies reach the server. Clear VITE_NOTIFICATION_SERVICE_WS_URL or point it at the SPA origin in local dev.',
        configured,
        origin,
      })
      return origin
    }
  } catch {
    return configured
  }
  return configured
}

/**
 * Opens a Socket.io connection to the notification service and invalidates
 * bell notification queries whenever a `notification:new` event arrives.
 *
 * The hook is a no-op when:
 *  - The user is not authenticated / has no tenant subdomain, or
 *  - In SSR / non-browser there is no origin to connect to.
 *
 * Call once at the AppShell level so the socket stays alive for the whole session.
 */
export function useNotificationSocket(): void {
  const { user } = useSessionStore()
  const queryClient = useQueryClient()
  const socketRef = useRef<Socket | null>(null)
  const queryClientRef = useRef<QueryClient>(queryClient)
  queryClientRef.current = queryClient

  const wsUrl = getNotificationSocketBaseUrl()
  const subdomain = user?.subdomain

  useEffect(() => {
    if (!wsUrl || !subdomain) return

    const socket = retainBellSocket(wsUrl, subdomain)
    socketRef.current = socket

    const onNew = (payload?: { type?: string }): void => {
      queryClientRef.current.invalidateQueries({ queryKey: queryKeys.notifications.all() })
      const type = typeof payload === 'object' && payload?.type ? payload.type : ''
      const shouldRefreshDashboard =
        !type ||
        /campaign|team|reward|invitation|member/i.test(type)
      if (shouldRefreshDashboard) {
        queryClientRef.current.invalidateQueries({ queryKey: ['admin-dashboard'] })
        queryClientRef.current.invalidateQueries({ queryKey: ['campaigns'] })
      }
    }
    const onConnect = (): void => {
      logger.info('notification_socket.connected', { subdomain })
    }
    const onConnectError = (err: Error): void => {
      logger.warn('notification_socket.connect_error', { err: err.message })
    }
    const onDisconnect = (reason: string): void => {
      logger.info('notification_socket.disconnected', { reason })
    }

    socket.on('notification:new', onNew)
    socket.on('connect', onConnect)
    socket.on('connect_error', onConnectError)
    socket.on('disconnect', onDisconnect)

    return () => {
      socket.off('notification:new', onNew)
      socket.off('connect', onConnect)
      socket.off('connect_error', onConnectError)
      socket.off('disconnect', onDisconnect)
      releaseBellSocket()
      socketRef.current = null
    }
  }, [wsUrl, subdomain])
}
